import SignApi from "../api/sign/SignApi";
import generateRequestId from "../utils/generateRequestId";
import CertificateInfo from "../api/sign/types/CertificateInfo";
import SigningData from "../api/sign/types/SigningData";
import {ModuleCode} from "../api/sign/ModuleCode";
import {getUrl, signDocumentFile} from "./marbesSignActions";
import ModuleMeta from "../api/sign/types/ModuleMeta";
import Status from "../api/sign/types/Status";

/**
 * Detailní stav podepisování.
 */
export interface StatusChange {
    /**
     * Identifikátor podepisování.
     */
    signingId: string;

    /**
     * Stav průběhu.
     */
    status: Status;

    /**
     * Rozšiřující data modulu.
     */
    moduleData?: ModuleMeta;

    /**
     * Chybová zpráva.
     */
    error?: string;

    /**
     * Detailní zpráva.
     */
    message?: string;

    /**
     * Průběh podepisování (v %, od 0 do 100)
     */
    progress: number;

    /**
     * Počet podepsaných dokumentů.
     */
    signed?: number;

    /**
     * Celkový počet dokumentů k podepsání.
     */
    total?: number;
}

/**
 * Instance podepisování.
 */
export interface Signing {
    /**
     * Unikátní identifikátor podepisování.
     */
    uuid: string;

    /**
     * Zahájení podepisování.
     *
     * @param cert certifikát pro podepsání
     * @param data rozšiřující data pro podepsání
     */
    start: (cert: CertificateInfo, data?: SigningData) => void;

    /**
     * Zrušení podepisování.
     */
    cancel: () => void;

    /**
     * Získání aktuálního průběhu podepisování.
     */
    status: () => StatusChange;
}

export const defaultOnFatal = (error: string) => {
    console.error("Nedefinovaný callback defaultOnFatal", error);
}

export const defaultOnStatus = (status: StatusChange) => {
    console.error("Nedefinovaný callback onStatus", status);
}

/**
 * Založení instance podepisování.
 *
 * @param onStatus          callback pro změnu stavu průběhu podepisování
 * @param startSigningCall  callback pro zahájení podepisování (server api)
 * @param statusSigningCall callback pro získání stavu (server api)
 * @param cancelSigningCall callback pro zrušení podepisování (server api)
 * @param onFatal           callback pro fatální selhání
 * @param url               url serveru podepisování
 */
export function createSigning(onStatus = defaultOnStatus,
                              startSigningCall = SignApi.startSigning,
                              statusSigningCall = SignApi.statusSigning,
                              cancelSigningCall = SignApi.cancelSigning,
                              onFatal = defaultOnFatal,
                              url: string = getUrl()): Signing {
    const uuid = generateRequestId();

    const onStatusInt = (statusChange) => {
        onStatus({...statusChange});
    }

    const statusChange: StatusChange = {
        signingId: uuid,
        status: Status.INIT,
        message: "Probíhá inicializace podepisování.",
        progress: 0
    }

    let statusCallTimeout = null;
    const cleanStatusCall = () => {
        if (statusCallTimeout != null) {
            clearInterval(statusCallTimeout);
            statusCallTimeout = null;
        }
    }

    const startStatusCall = () => {
        //console.debug("statusCallTimeout");
        if (statusChange.status == Status.ERROR || statusChange.status == Status.CANCEL) {
            console.debug("statusCallTimeout stop on ERROR");
            return null;
        }
        statusSigningCall(statusChange.signingId).then(signingStatus => {
            statusChange.status = signingStatus.status;
            statusChange.message = signingStatus.message;
            statusChange.progress = signingStatus.progress;
            statusChange.signed = signingStatus.signed;
            statusChange.total = signingStatus.total;
            if (!statusChange.error && signingStatus.errorMessage) {
                statusChange.error = signingStatus.errorMessage;
            }
            onStatusInt(statusChange);
            if (statusChange.status == Status.FINISH) {
                console.info("Podepisování dokončeno", statusChange.signingId);
                cleanStatusCall();
                return;
            }
            statusCallTimeout = setTimeout(startStatusCall, 1000);
        }).catch((error) => {
            console.debug(error);
            statusChange.status = Status.ERROR;
            statusChange.error = "Selhalo zjištění stavu podepisování.";
            onStatusInt(statusChange);
        });
    }

    const startFce = (cert, data) => {
        onStatusInt(statusChange);
        const callSigning = () => {
            console.log("callSigning", uuid);
            startSigningCall({
                signingId: uuid,
                certificateInfo: cert,
                data: data
            }).then(signingResult => {
                statusChange.signingId = signingResult.signingId;
                statusChange.moduleData = signingResult.moduleMeta;
                onStatusInt(statusChange);
                startStatusCall();
            }).catch(error => {
                console.debug(error);
                statusChange.status = Status.ERROR;
                statusChange.error = "Nepodařilo se vytvořit požadavek na podepsání.";
                onStatusInt(statusChange);
            });
        };
        if (cert.moduleCode == ModuleCode.MARBES) {
            signDocumentFile(uuid, cert.thumbprint, url)
                .then(() => {
                    callSigning();
                })
                .catch(error => {
                    onFatal(error);
                    statusChange.status = Status.ERROR;
                    statusChange.error = "Nepodařilo se inicializovat plugin na podepisování.";
                    onStatusInt(statusChange);
                });
        } else {
            callSigning();
        }
    };

    const cancelFce = () => {
        cleanStatusCall();
        return cancelSigningCall(statusChange.signingId)
            .then(() => {
                statusChange.status = Status.CANCEL;
                statusChange.message = "Podepisování bylo zrušeno podepisujícím.";
                onStatusInt(statusChange);
            })
            .catch(() => {
                statusChange.status = Status.CANCEL;
                statusChange.message = "Podepisování bylo zrušeno podepisujícím.";
                onStatusInt(statusChange);
            });
    }

    const statusFce = () => {
        return statusChange;
    }

    return {
        uuid: uuid,
        start: startFce,
        cancel: cancelFce,
        status: statusFce,
    }
}

export const getActiveModules = SignApi.getActiveModules;

export const getCertificates = SignApi.getCertificates;
